'use client';

import { useState, useEffect, FormEvent, useRef } from 'react';
import { useGoogleStt } from '@/hooks/use-google-stt'; // 경로 확인
import { Send, Mic, StopCircle, X } from 'lucide-react'; // 아이콘
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils'; // cn 유틸리티

interface ChatInputProps {
  onSend: (text: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

export default function ChatInput({ onSend, disabled = false, placeholder }: ChatInputProps) {
  const [text, setText] = useState('');
  const { text: voiceText, listening, start, stop } = useGoogleStt();
  const [elapsed, setElapsed] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // --- 기능 관련 로직 (변경 없음) ---
  useEffect(() => {
    if (!listening) return;
    setElapsed(0);
    const id = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [listening]);

  useEffect(() => {
    if (listening) {
      setText(voiceText);
      if (inputRef.current) {
        requestAnimationFrame(() => {
          if (inputRef.current) {
            inputRef.current.scrollLeft = inputRef.current.scrollWidth;
          }
        });
      }
    }
  }, [voiceText, listening]);

  const handleSubmit = (e?: FormEvent) => {
    if (e) e.preventDefault();
    const trimmedText = text.trim();
    if (!trimmedText || listening) return;
    onSend(trimmedText);
    setText('');
  };

  const handleMicClick = () => {
    if (listening) {
      stop();
    } else {
      setText('');
      start();
      inputRef.current?.focus();
    }
  };

  const handleCancelRecording = () => {
    stop();
    setText('');
  };

  const secToTime = (s: number) => {
    const minutes = Math.floor(s / 60);
    const seconds = s % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };
  // --- 기능 관련 로직 끝 ---


  // --- UI 렌더링 시작 (X 버튼 위치/스타일 수정) ---
  return (
    // 전체 컨테이너: X 버튼을 포함할 수 있도록 약간의 왼쪽 공간 확보 고려 (필요시)
    <div className="relative w-full flex items-center gap-2"> {/* form과 X버튼을 위한 flex 컨테이너 */}

      {/* X 버튼 (녹음 중일 때만 왼쪽에 표시) */}
      <Button
        type="button"
        variant="ghost"
        size="icon" // size="icon" 유지하되, 크기 직접 지정
        onClick={handleCancelRecording}
        className={cn(
          "h-8 w-8 rounded-full transition-all duration-300 ease-in-out flex-shrink-0", // 크기 줄임, 줄어들지 않도록
          "text-gray-500 hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-gray-700",
          // 녹음 중일 때만 표시 (opacity, scale, width)
          listening
            ? "opacity-100 scale-100 w-8" // 나타날 때 크기
            : "opacity-0 scale-75 w-0 pointer-events-none" // 숨겨질 때 크기 0
        )}
        style={{ padding: 0 }} // 내부 아이콘 패딩 제거하여 꽉 차게
        disabled={disabled || !listening}
        aria-label="녹음 취소"
      >
        <X className="h-4 w-4" /> {/* 아이콘 크기도 줄임 */}
      </Button>

      {/* 기존 입력 폼 */}
      <form
        onSubmit={handleSubmit}
        // form 자체는 남은 공간을 모두 사용
        className={cn(
          "flex-1 flex items-center gap-2 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-full shadow-lg transition-colors duration-300 ease-in-out", // 배경 전환 효과 추가 가능
          "h-14 sm:h-16",
          "px-2 sm:px-3" // 내부 패딩
          // 녹음 중 배경색 변경 등의 효과는 여기서 제어 가능
          // listening && "bg-gray-100 dark:bg-gray-700"
        )}
      >
        {/* --- 입력 필드 / 타이머 영역 --- */}
        <div className="flex-1 flex items-center h-full overflow-hidden relative pr-1">
          <Input
            ref={inputRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={listening ? "듣는 중..." : placeholder || "메시지를 입력하세요..."}
            className={cn(
              "w-full h-full rounded-full pl-4 pr-8 sm:pl-5 sm:pr-10 border-none bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 shadow-none transition-colors duration-300 ease-in-out",
              "text-sm sm:text-base",
              disabled && "opacity-50 cursor-not-allowed",
              listening
                ? "placeholder-gray-500 dark:placeholder-gray-400"
                : "placeholder-gray-400 dark:placeholder-gray-500",
            )}
            autoComplete="off"
            disabled={disabled}
            style={{ caretColor: listening ? 'transparent' : 'auto' }}
            readOnly={listening && !disabled}
          />
          <div
            className={cn(
              "absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 flex items-center transition-opacity duration-300 ease-in-out",
              listening ? "opacity-100" : "opacity-0 pointer-events-none"
            )}
          >
            <span className="text-xs sm:text-sm tabular-nums text-gray-500 dark:text-gray-400 font-mono">
              {secToTime(elapsed)}
            </span>
          </div>
        </div>

        {/* --- 오른쪽 버튼 영역 (마이크/중지, 전송) --- */}
        {/* X 버튼이 이 그룹에서 제거됨 */}
        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleMicClick}
            disabled={disabled}
            className="h-10 w-10 sm:h-11 sm:w-11 rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
            aria-label={listening ? "녹음 중지" : "녹음 시작"}
          >
            {listening ? (
              <StopCircle className="h-5 w-5 sm:h-6 sm:w-6 text-red-500" />
            ) : (
              <Mic className="h-5 w-5 sm:h-6 sm:w-6" />
            )}
          </Button>

          {/* 전송 버튼 (항상 오른쪽 끝에 위치) */}
          {/* 녹음 중이 아닐 때만 보이도록 처리 */}
          <Button
            type="submit"
            size="icon"
            className={cn(
              "h-10 w-10 sm:h-11 sm:w-11 rounded-full gradient-button transition-all duration-300 ease-in-out flex items-center justify-center",
              // 녹음 중이거나 텍스트 없으면 숨김 (X 버튼 전환 로직 제거)
              listening || !text.trim()
                ? "opacity-0 scale-75 pointer-events-none w-0" // 너비도 0으로 만들어 공간 차지 안함
                : "opacity-100 scale-100 pointer-events-auto w-10 sm:w-11" // 기본 크기
            )}
            disabled={disabled || listening || !text.trim()}
            aria-label="보내기"
          >
            <Send className="h-5 w-5" />
          </Button>
        </div>
      </form>
    </div>
  );
}

// --- `cn` 유틸리티 함수 예시 ---
// import { clsx } from "clsx"
// import { twMerge } from "tailwind-merge"
// export function cn(...inputs) {
//   return twMerge(clsx(inputs))
// }